/**
 * KdpMetadataDialog — AI-assisted KDP (Kindle Direct Publishing) metadata form.
 *
 * Three tabs:
 *  1. Identity      — title, subtitle, series, author, language
 *  2. Discoverability — description (4 000 char, HTML), keywords (≤ 7), BISAC (≤ 3)
 *  3. Settings      — adult content, KDP Select, age range
 *
 * AI panel (always visible):
 *  — "Extract from manuscript" button → background job → auto-populates all tabs
 *  — Shows model name, elapsed time, field-level AI badges
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, ChevronDown, ChevronUp, Info, Loader2, Plus,
  Sparkles, Tag, Trash2, X, Check, AlertCircle, ShieldCheck, ShieldAlert,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button }   from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge }    from '@/components/ui/badge';
import { cn }       from '@/lib/utils';
import { BISAC_CATEGORIES } from '@/lib/bisacCategories';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

// ─── KDP Compliance rules ────────────────────────────────────────────────────
const KDP_ALLOWED_HTML = new Set(['b', 'i', 'em', 'strong', 'br', 'u', 'p', 'ul', 'ol', 'li', 'h4', 'h5', 'h6']);

function computeViolations(form) {
  const issues = [];
  const err  = (msg) => issues.push({ level: 'error', msg });
  const warn = (msg) => issues.push({ level: 'warn',  msg });

  // Required fields
  if (!form.title?.trim())        err('Title is required.');
  if ((form.title || '').length > 200) err('Title must be 200 characters or fewer.');
  if ((form.subtitle || '').length > 200) err('Subtitle must be 200 characters or fewer.');
  if (!form.author_name?.trim())  err('Author / Contributor name is required.');
  if (!form.description?.trim())  err('Book description is required.');
  if ((form.description || '').length > 4000) err('Description exceeds the 4,000-character limit.');

  // Age range
  const amin = form.age_min ? Number(form.age_min) : null;
  const amax = form.age_max ? Number(form.age_max) : null;
  if ((amin !== null) !== (amax !== null)) warn('Set both minimum and maximum age, or leave both blank.');
  if (amin !== null && amax !== null && amin > amax) err('Minimum age cannot be greater than maximum age.');

  // Keywords
  if (form.keywords?.length > 7) err('KDP allows a maximum of 7 search keywords.');
  (form.keywords || []).forEach((kw) => {
    if (kw.length > 50) warn(`Keyword "${kw.slice(0, 30)}…" exceeds 50 characters.`);
  });

  // BISAC categories
  if (!form.categories?.length) warn('Add at least one BISAC category to improve discoverability.');

  // HTML tags in description
  const descHtml = form.description || '';
  const tagMatches = [...descHtml.matchAll(/<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g)];
  const disallowed = [...new Set(tagMatches.map((m) => m[1].toLowerCase()).filter((t) => !KDP_ALLOWED_HTML.has(t)))];
  if (disallowed.length) warn(`Description contains HTML tags not supported by KDP: ${disallowed.map((t) => `<${t}>`).join(', ')}.`);

  return issues;
}

// ─── Field wrapper with AI badge ─────────────────────────────────────────────
function Field({ label, hint, children, aiTouched, required }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <label className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}{required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        {aiTouched && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
            <Sparkles className="h-2.5 w-2.5" /> AI suggestion
          </span>
        )}
        {hint && (
          <span className="ml-auto text-[11px] text-muted-foreground">{hint}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, maxLength, className, testId }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      data-testid={testId}
      className={cn(
        'w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px]',
        'placeholder:text-muted-foreground/50',
        'focus:outline-none focus:ring-1 focus:ring-primary',
        className,
      )}
    />
  );
}

// ─── Keyword chips ────────────────────────────────────────────────────────────
function KeywordInput({ keywords, onChange }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v || keywords.includes(v) || keywords.length >= 7) return;
    onChange([...keywords, v]);
    setDraft('');
  };
  const remove = (kw) => onChange(keywords.filter(k => k !== kw));
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[32px]">
        {keywords.map(kw => (
          <span
            key={kw}
            className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[12px]"
          >
            <Tag className="h-3 w-3 text-muted-foreground shrink-0" />
            {kw}
            <button onClick={() => remove(kw)} className="text-muted-foreground hover:text-destructive ml-0.5">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {keywords.length < 7 && (
          <span className="text-[11px] text-muted-foreground self-center pl-1">
            {7 - keywords.length} remaining
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Type a keyword, press Enter"
          maxLength={50}
          disabled={keywords.length >= 7}
          className="flex-1 rounded-lg border border-border bg-background px-3 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          data-testid="kdp-keyword-input"
        />
        <Button size="sm" variant="outline" className="h-8" onClick={add} disabled={!draft.trim() || keywords.length >= 7}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

// ─── BISAC picker ──────────────────────────────────────────────────────────────
function BisacPicker({ categories, onChange }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = q.length >= 1
    ? BISAC_CATEGORIES.filter(c =>
        c.name.toLowerCase().includes(q.toLowerCase()) ||
        c.path.toLowerCase().includes(q.toLowerCase()) ||
        c.code.toLowerCase().includes(q.toLowerCase())
      )
    : BISAC_CATEGORIES;

  const toggle = (cat) => {
    if (categories.find(c => c.code === cat.code)) {
      onChange(categories.filter(c => c.code !== cat.code));
    } else if (categories.length < 3) {
      onChange([...categories, cat]);
    } else {
      toast.error('Maximum 3 BISAC categories allowed.');
    }
  };

  return (
    <div className="space-y-2">
      {/* Selected chips */}
      <div className="flex flex-wrap gap-1.5 min-h-[30px]">
        {categories.map(cat => (
          <span
            key={cat.code}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11.5px] font-medium"
          >
            <BookOpen className="h-3 w-3 shrink-0" />
            <span className="max-w-[200px] truncate">{cat.name}</span>
            <button onClick={() => toggle(cat)} className="ml-0.5 hover:text-destructive">
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {categories.length === 0 && (
          <span className="text-[11.5px] text-muted-foreground self-center">No categories selected</span>
        )}
      </div>

      {/* Search + dropdown */}
      <div className="relative">
        <input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={categories.length >= 3 ? 'Max 3 categories' : 'Search BISAC categories…'}
          disabled={categories.length >= 3}
          className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
          data-testid="kdp-bisac-search"
        />
        {open && categories.length < 3 && (
          <div className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto rounded-lg border border-border bg-card shadow-xl">
            {filtered.slice(0, 30).map(cat => {
              const selected = !!categories.find(c => c.code === cat.code);
              return (
                <button
                  key={cat.code}
                  type="button"
                  onClick={() => { toggle(cat); setQ(''); setOpen(false); }}
                  className={cn(
                    'flex w-full items-start gap-2 px-3 py-2 text-left text-[12.5px] hover:bg-muted/50 transition-colors',
                    selected && 'bg-primary/5',
                  )}
                  data-testid={`bisac-option-${cat.code}`}
                >
                  <div className={cn('mt-0.5 h-4 w-4 shrink-0 rounded border flex items-center justify-center',
                    selected ? 'bg-primary border-primary text-white' : 'border-muted-foreground/40')}>
                    {selected && <Check className="h-2.5 w-2.5" />}
                  </div>
                  <div>
                    <p className="font-medium leading-tight">{cat.name}</p>
                    <p className="text-[10.5px] text-muted-foreground mt-0.5">{cat.path}</p>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-3 py-3 text-[12.5px] text-muted-foreground text-center">No categories found</p>
            )}
          </div>
        )}
        {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
      </div>
      {categories.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {categories.map(c => c.code).join(' · ')}
        </p>
      )}
    </div>
  );
}

// ─── AI extraction strip ──────────────────────────────────────────────────────
function AiStrip({ projectId, onResult, isRunning, elapsed, error }) {
  return (
    <div className={cn(
      'rounded-xl border px-4 py-3 flex items-center gap-3',
      isRunning
        ? 'border-primary/30 bg-primary/5'
        : error
          ? 'border-destructive/30 bg-destructive/5'
          : 'border-border bg-muted/30',
    )}>
      <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
        isRunning ? 'bg-primary/10' : 'bg-muted')}>
        {isRunning
          ? <Loader2 className="h-4 w-4 text-primary animate-spin" />
          : error
            ? <AlertCircle className="h-4 w-4 text-destructive" />
            : <Sparkles className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold leading-tight">
          {isRunning ? 'Analysing manuscript…' : error ? 'Extraction failed' : 'AI Extraction'}
        </p>
        <p className="text-[11.5px] text-muted-foreground mt-0.5 leading-snug">
          {isRunning
            ? `Claude is reading your manuscript — ${elapsed}s elapsed. All fields will be populated automatically.`
            : error
              ? error
              : 'Click to let Claude suggest your description, keywords and BISAC categories from the manuscript.'}
        </p>
      </div>
      <Button
        size="sm"
        variant={isRunning ? 'outline' : 'default'}
        className="shrink-0 gap-1.5 h-8"
        onClick={onResult}
        disabled={isRunning}
        data-testid="kdp-extract-btn"
      >
        {isRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        {isRunning ? 'Running…' : 'Extract from manuscript'}
      </Button>
    </div>
  );
}

// ─── Main dialog ─────────────────────────────────────────────────────────────
export default function KdpMetadataDialog({ open, onOpenChange, projectId }) {
  const empty = {
    title: '', subtitle: '', series_name: '', series_number: '',
    author_name: '', description: '', keywords: [], categories: [],
    language: 'en', adult_content: false, kdp_select: false,
    age_min: '', age_max: '',
  };
  const [form,      setForm]      = useState(empty);
  const [aiFields,  setAiFields]  = useState(new Set()); // which fields were AI-populated
  const [saving,    setSaving]    = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [extracting,setExtracting]= useState(false);
  const [elapsed,   setElapsed]   = useState(0);
  const [extractErr,setExtractErr]= useState(null);
  const pollRef  = useRef(null);
  const timerRef = useRef(null);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Load form ──
  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await http.get(`/projects/${projectId}/kdp-form`);
      setForm({ ...empty, ...r.data });
      setAiFields(new Set());
    } catch {
      toast.error('Could not load KDP form.');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) load(); }, [open, load]);

  // ── Save ──
  const save = async () => {
    setSaving(true);
    try {
      await http.put(`/projects/${projectId}/kdp-form`, {
        ...form,
        age_min: form.age_min ? Number(form.age_min) : null,
        age_max: form.age_max ? Number(form.age_max) : null,
      });
      toast.success('KDP metadata saved.');
    } catch {
      toast.error('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Copy for KDP ──
  const copyForKdp = () => {
    const cats = form.categories.map(c => `  • ${c.path} (${c.code})`).join('\n');
    const kws  = form.keywords.join(', ');
    const text = [
      `TITLE: ${form.title}`,
      form.subtitle && `SUBTITLE: ${form.subtitle}`,
      form.series_name && `SERIES: ${form.series_name} #${form.series_number || ''}`.trim(),
      `AUTHOR: ${form.author_name}`,
      '',
      `DESCRIPTION:\n${form.description}`,
      '',
      `KEYWORDS:\n${kws}`,
      '',
      `BISAC CATEGORIES:\n${cats}`,
      '',
      `LANGUAGE: ${form.language}`,
      `ADULT CONTENT: ${form.adult_content ? 'Yes' : 'No'}`,
      `KDP SELECT: ${form.kdp_select ? 'Yes' : 'No'}`,
      form.age_min && `AGE RANGE: ${form.age_min}–${form.age_max}`,
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(text);
    toast.success('KDP metadata copied to clipboard.');
  };

  // ── AI Extraction ──
  const startExtraction = async () => {
    setExtracting(true);
    setExtractErr(null);
    setElapsed(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed(n => n + 1), 1000);
    try {
      const r    = await http.post(`/projects/${projectId}/kdp-form/extract`);
      const jid  = r.data.job_id;
      pollRef.current = setInterval(async () => {
        try {
          const p = await http.get(`/projects/${projectId}/kdp-form/jobs/${jid}`);
          if (p.data.status === 'done') {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            const res = p.data.result || {};
            // Merge AI result into form, track which fields changed
            const touched = new Set();
            setForm(prev => {
              const next = { ...prev };
              const adopt = (key, val) => {
                if (val !== undefined && val !== null && val !== '') {
                  next[key] = val;
                  touched.add(key);
                }
              };
              adopt('title',         res.title);
              adopt('subtitle',      res.subtitle);
              adopt('series_name',   res.series_name);
              adopt('series_number', res.series_number);
              adopt('description',   res.description);
              if (Array.isArray(res.keywords) && res.keywords.length)   { next.keywords   = res.keywords;   touched.add('keywords'); }
              if (Array.isArray(res.categories) && res.categories.length){ next.categories = res.categories; touched.add('categories'); }
              if (res.adult_content !== undefined) { next.adult_content = res.adult_content; touched.add('adult_content'); }
              if (res.language)                    { next.language       = res.language;      touched.add('language'); }
              if (res.age_min)    { next.age_min = String(res.age_min);  touched.add('age_min'); }
              if (res.age_max)    { next.age_max = String(res.age_max);  touched.add('age_max'); }
              return next;
            });
            setAiFields(touched);
            setExtracting(false);
            toast.success('Manuscript extracted — review the AI suggestions below.');
          } else if (p.data.status === 'failed') {
            clearInterval(pollRef.current);
            clearInterval(timerRef.current);
            setExtractErr(p.data.error || 'Extraction failed.');
            setExtracting(false);
          }
        } catch { /* poll errors are transient */ }
      }, 2000);
    } catch (e) {
      clearInterval(timerRef.current);
      setExtractErr(e?.response?.data?.detail || 'Could not start extraction.');
      setExtracting(false);
    }
  };

  useEffect(() => () => { clearInterval(pollRef.current); clearInterval(timerRef.current); }, []);

  const descLen  = (form.description || '').length;
  const descWarn = descLen > 3800;

  const violations = useMemo(() => computeViolations(form), [form]);
  const errCount  = violations.filter((v) => v.level === 'error').length;
  const warnCount = violations.filter((v) => v.level === 'warn').length;

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col gap-0 p-0"
        data-testid="kdp-metadata-dialog"
      >
        {/* ── Header ── */}
        <DialogHeader className="px-5 pt-5 pb-3 shrink-0 border-b border-border">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-[15px] font-semibold flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              KDP Metadata Form
            </DialogTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-8 text-[12px] gap-1.5" onClick={copyForKdp}
                data-testid="kdp-copy-btn" disabled={!form.title}>
                Copy for KDP
              </Button>
              <Button size="sm" className="h-8 text-[12px] gap-1.5" onClick={save} disabled={saving}
                data-testid="kdp-save-btn">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Save
              </Button>
            </div>
          </div>
          <p className="text-[12px] text-muted-foreground mt-0.5">
            Fill in all fields required by Amazon KDP. Use AI Extraction to pre-populate suggestions from the manuscript.
          </p>
        </DialogHeader>

        {/* ── AI strip ── */}
        <div className="px-5 pt-3 pb-2 shrink-0">
          <AiStrip
            projectId={projectId}
            onResult={startExtraction}
            isRunning={extracting}
            elapsed={elapsed}
            error={extractErr}
          />
        </div>

        {/* ── Tabs ── */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Tabs defaultValue="identity" className="h-full flex flex-col">
              <TabsList className="mx-5 shrink-0 h-9 mb-0">
                <TabsTrigger value="identity"    className="text-[12.5px]" data-testid="kdp-tab-identity">Identity</TabsTrigger>
                <TabsTrigger value="discovery"   className="text-[12.5px]" data-testid="kdp-tab-discovery">Discoverability</TabsTrigger>
                <TabsTrigger value="settings"    className="text-[12.5px]" data-testid="kdp-tab-settings">Settings</TabsTrigger>
                <TabsTrigger value="compliance"  className="relative text-[12.5px]" data-testid="kdp-tab-compliance">
                  Compliance
                  {errCount > 0 && (
                    <span className="ml-1 rounded-full bg-destructive px-1 py-0.5 text-[9px] font-bold text-destructive-foreground leading-none">{errCount}</span>
                  )}
                  {errCount === 0 && warnCount > 0 && (
                    <span className="ml-1 rounded-full bg-amber-500 px-1 py-0.5 text-[9px] font-bold text-white leading-none">{warnCount}</span>
                  )}
                </TabsTrigger>
              </TabsList>

              {/* ── IDENTITY ── */}
              <TabsContent value="identity" className="flex-1 overflow-y-auto px-5 py-4 space-y-4 mt-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Field label="Book Title" required aiTouched={aiFields.has('title')}>
                      <TextInput value={form.title} onChange={v => set('title', v)} placeholder="Your book title" testId="kdp-title" />
                    </Field>
                  </div>
                  <div className="col-span-2">
                    <Field label="Subtitle" aiTouched={aiFields.has('subtitle')}>
                      <TextInput value={form.subtitle} onChange={v => set('subtitle', v)} placeholder="Optional subtitle" testId="kdp-subtitle" />
                    </Field>
                  </div>
                  <Field label="Author / Primary Contributor" required aiTouched={aiFields.has('author_name')}>
                    <TextInput value={form.author_name} onChange={v => set('author_name', v)} placeholder="First Last" testId="kdp-author" />
                  </Field>
                  <Field label="Language" aiTouched={aiFields.has('language')}>
                    <select
                      value={form.language}
                      onChange={e => set('language', e.target.value)}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                      data-testid="kdp-language"
                    >
                      <option value="en">English</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="es">Spanish</option>
                      <option value="it">Italian</option>
                      <option value="pt">Portuguese</option>
                      <option value="nl">Dutch</option>
                      <option value="ja">Japanese</option>
                      <option value="zh">Chinese</option>
                      <option value="ko">Korean</option>
                    </select>
                  </Field>
                  <Field label="Series Name" aiTouched={aiFields.has('series_name')}>
                    <TextInput value={form.series_name} onChange={v => set('series_name', v)} placeholder="The Chronicles of…" testId="kdp-series-name" />
                  </Field>
                  <Field label="Series Number" aiTouched={aiFields.has('series_number')}>
                    <TextInput value={form.series_number} onChange={v => set('series_number', v)} placeholder="1" testId="kdp-series-number" />
                  </Field>
                </div>

                {/* Copyright statement */}
                <div className="rounded-lg border border-border bg-muted/30 p-3 flex items-start gap-2 text-[12px] text-muted-foreground">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                  <span>
                    By submitting to KDP you confirm you hold all publishing rights and this is not
                    a public-domain work reproduced without meaningful transformation.
                  </span>
                </div>
              </TabsContent>

              {/* ── DISCOVERABILITY ── */}
              <TabsContent value="discovery" className="flex-1 overflow-y-auto px-5 py-4 space-y-5 mt-0">
                <Field
                  label="Book Description"
                  hint={<span className={cn(descWarn ? 'text-amber-500 font-semibold' : '')}>{descLen}/4 000</span>}
                  aiTouched={aiFields.has('description')}
                  required
                >
                  <textarea
                    value={form.description}
                    onChange={e => set('description', e.target.value)}
                    maxLength={4000}
                    rows={9}
                    placeholder="Write a compelling marketing blurb — hook in the first sentence, no spoilers, end with a call-to-action. HTML is supported: <b>, <i>, <br>, <ul>, <li>."
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] leading-relaxed resize-y focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="kdp-description"
                  />
                  {descWarn && (
                    <p className="text-[11px] text-amber-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Approaching the 4 000 character limit. KDP will truncate anything beyond.
                    </p>
                  )}
                </Field>

                <Field label="Search Keywords" hint="Up to 7 — each up to 50 chars" aiTouched={aiFields.has('keywords')}>
                  <KeywordInput keywords={form.keywords} onChange={v => set('keywords', v)} />
                </Field>

                <Field label="BISAC Categories" hint="Up to 3" aiTouched={aiFields.has('categories')}>
                  <BisacPicker categories={form.categories} onChange={v => set('categories', v)} />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Amazon maps BISAC codes to its own browse tree. Choose the most specific codes
                    that match your book&apos;s primary genre and sub-genre.
                  </p>
                </Field>
              </TabsContent>

              {/* ── SETTINGS ── */}
              <TabsContent value="settings" className="flex-1 overflow-y-auto px-5 py-4 space-y-5 mt-0">
                {/* Adult content */}
                <div className="flex items-start justify-between rounded-xl border border-border p-4">
                  <div>
                    <p className="text-[13px] font-semibold">Adult Content</p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      Check this if the book contains explicit sexual content. KDP will restrict
                      it to adult searches and hide it from under-18 browse lists.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.adult_content}
                    onChange={e => set('adult_content', e.target.checked)}
                    className="h-4 w-4 mt-1 accent-primary cursor-pointer"
                    data-testid="kdp-adult-content"
                  />
                </div>

                {/* KDP Select */}
                <div className="flex items-start justify-between rounded-xl border border-border p-4">
                  <div>
                    <p className="text-[13px] font-semibold">KDP Select / Kindle Unlimited</p>
                    <p className="text-[12px] text-muted-foreground mt-0.5">
                      Enrol for 90-day exclusivity in exchange for Kindle Unlimited reads and
                      promotional tools (Countdown Deals, Free Days). Requires digital exclusivity
                      on Amazon — cannot be sold elsewhere as an eBook during enrolment.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.kdp_select}
                    onChange={e => set('kdp_select', e.target.checked)}
                    className="h-4 w-4 mt-1 accent-primary cursor-pointer"
                    data-testid="kdp-select"
                  />
                </div>

                {/* Age & Grade Range */}
                <Field label="Age Range (optional — mainly for children's / YA)">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-[11px] text-muted-foreground mb-1">Minimum age</p>
                      <input
                        type="number" min={0} max={18}
                        value={form.age_min || ''}
                        onChange={e => set('age_min', e.target.value)}
                        placeholder="—"
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                        data-testid="kdp-age-min"
                      />
                    </div>
                    <span className="text-muted-foreground mt-5">to</span>
                    <div className="flex-1">
                      <p className="text-[11px] text-muted-foreground mb-1">Maximum age</p>
                      <input
                        type="number" min={0} max={18}
                        value={form.age_max || ''}
                        onChange={e => set('age_max', e.target.value)}
                        placeholder="—"
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
                        data-testid="kdp-age-max"
                      />
                    </div>
                  </div>
                </Field>

                {/* Publishing rights declaration */}
                <div className="rounded-xl bg-amber-500/8 border border-amber-500/20 p-4 text-[12px] text-muted-foreground space-y-1">
                  <p className="font-semibold text-foreground">Publishing Rights Checklist</p>
                  {[
                    'I own the copyright or have permission to publish this work.',
                    'This is not a public-domain work reproduced without meaningful transformation.',
                    'The cover art and all images are licensed or original.',
                    'The book description does not make false claims or use misleading keywords.',
                    'Any real people, trademarks, or brands mentioned are used lawfully.',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* ── COMPLIANCE ── */}
              <TabsContent value="compliance" className="flex-1 overflow-y-auto px-5 py-4 mt-0" data-testid="kdp-compliance-tab">
                <div className="mb-3 flex items-center gap-2">
                  {errCount === 0 && warnCount === 0
                    ? <ShieldCheck className="h-5 w-5 text-emerald-600" />
                    : errCount > 0
                      ? <ShieldAlert className="h-5 w-5 text-destructive" />
                      : <ShieldAlert className="h-5 w-5 text-amber-500" />
                  }
                  <div>
                    <p className="text-[13px] font-semibold">
                      {errCount === 0 && warnCount === 0
                        ? 'All checks passed'
                        : `${errCount} error${errCount !== 1 ? 's' : ''}, ${warnCount} warning${warnCount !== 1 ? 's' : ''}`}
                    </p>
                    <p className="text-[11.5px] text-muted-foreground mt-0.5">
                      Resolve errors before publishing. Warnings are advisory but improve discoverability and compliance.
                    </p>
                  </div>
                </div>

                {violations.length === 0 ? (
                  <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/8 p-4 text-[12.5px] text-emerald-700 dark:text-emerald-300">
                    No issues found — this metadata meets all checked Amazon KDP requirements.
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="kdp-violations-list">
                    {violations.map((v, i) => (
                      <div
                        key={i}
                        className={`flex items-start gap-2.5 rounded-lg border p-3 text-[12.5px] ${
                          v.level === 'error'
                            ? 'border-destructive/30 bg-destructive/5 text-destructive dark:text-red-400'
                            : 'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400'
                        }`}
                        data-testid={`kdp-violation-${i}`}
                      >
                        {v.level === 'error'
                          ? <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          : <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        }
                        <span>{v.msg}</span>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
